import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import type {
  CareerJob,
  CareerLookup,
  PublicCareersMeta,
} from '../services/api/types/careers';
import { isValidEmail, isValidMobileNumber } from '../utils/contactValidation';
import { LoginBackground } from '../components/auth/LoginBackground';

const EMPTY_APPLY = {
  name: '',
  email: '',
  phone: '',
  cover_letter: '',
};

function labelFor(items: CareerLookup[] | undefined, code: string) {
  return items?.find((item) => item.code === code)?.label ?? code;
}

export function CareersPage() {
  const [meta, setMeta] = useState<PublicCareersMeta | null>(null);
  const [jobs, setJobs] = useState<CareerJob[]>([]);
  const [q, setQ] = useState('');
  const [department, setDepartment] = useState('');
  const [location, setLocation] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [experience, setExperience] = useState('');
  const [selected, setSelected] = useState<CareerJob | null>(null);
  const [apply, setApply] = useState(EMPTY_APPLY);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);

  const accept = useMemo(
    () => (meta?.resume_extensions.length ? meta.resume_extensions.join(',') : '.pdf,.doc,.docx'),
    [meta],
  );

  async function loadJobs() {
    const res = await apiService.listPublicCareerJobs({
      q: q || undefined,
      department: department || undefined,
      location: location || undefined,
      employment_type: employmentType || undefined,
      experience: experience || undefined,
      limit: 50,
    });
    const items = res.data.items ?? [];
    setJobs(items);
    setSelected((current) => {
      if (!current) return items[0] ?? null;
      return items.find((item) => item.id === current.id) || items[0] || current;
    });
  }

  useEffect(() => {
    apiService
      .getPublicCareersMeta()
      .then((res) => setMeta(res.data))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    loadJobs().catch((err) => setError(err instanceof Error ? err.message : 'Could not load vacancies'));
  }, [q, department, location, employmentType, experience]);

  async function onApply(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setError('');
    if (!isValidEmail(apply.email)) {
      setError('Enter a valid email address');
      return;
    }
    if (apply.phone.trim() && !isValidMobileNumber(apply.phone)) {
      setError('Enter a valid mobile number');
      return;
    }
    if (!file) {
      setError('Attach your resume.');
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('job_id', selected.id);
      form.append('name', apply.name.trim());
      form.append('email', apply.email.trim());
      form.append('phone', apply.phone.trim());
      form.append('cover_letter', apply.cover_letter.trim());
      form.append('resume', file);
      await apiService.applyForCareerJob(form);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send application');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="login-page careers-public">
      <LoginBackground />
      <div className="login-page__content careers-public__content">
        <header className="careers-public__top">
          <Link to="/" className="font-display text-royal-ivory text-xl font-bold">
            SkoolFix
          </Link>
          <nav>
            <Link to="/login">Login</Link>
            <Link to="/careers">Careers</Link>
          </nav>
        </header>
        <section className="careers-public__hero">
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary-300/90 font-medium">Open roles</p>
          <h1 className="font-display text-4xl font-bold text-royal-ivory">Find a role that fits.</h1>
          <p className="text-primary-200/90 mt-2 max-w-2xl">
            Filter vacancies and apply with your resume. The hiring team is emailed the attachment.
          </p>
        </section>

        <form className="careers-public__filters" onSubmit={(e) => e.preventDefault()}>
          <input
            className="royal-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title or keyword"
          />
          <LookupSelect label="Department" value={department} onChange={setDepartment} options={meta?.departments} />
          <LookupSelect label="Location" value={location} onChange={setLocation} options={meta?.locations} />
          <LookupSelect label="Type" value={employmentType} onChange={setEmploymentType} options={meta?.employment_types} />
          <LookupSelect label="Experience" value={experience} onChange={setExperience} options={meta?.experience_levels} />
        </form>

        {error && !saving && <p className="text-red-200 text-sm mb-3">{error}</p>}

        <div className="careers-public__grid">
          <aside className="space-y-2">
            {jobs.length === 0 ? (
              <p className="text-primary-200/80 text-sm">No open vacancies match those filters.</p>
            ) : (
              jobs.map((job) => (
                <button
                  key={job.id}
                  type="button"
                  className={`careers-public__job${selected?.id === job.id ? ' is-on' : ''}`}
                  onClick={() => {
                    setSelected(job);
                    setSent(false);
                    setError('');
                  }}
                >
                  <strong>{job.title}</strong>
                  <span>
                    {labelFor(meta?.departments, job.department)} · {labelFor(meta?.locations, job.location)}
                  </span>
                </button>
              ))
            )}
          </aside>
          <section className="royal-card p-6">
            {!selected ? (
              <p className="text-secondary-600">Choose a vacancy to read the brief and apply.</p>
            ) : sent ? (
              <div>
                <h2 className="font-display text-2xl mb-2">Application sent</h2>
                <p className="text-secondary-600">
                  Thanks {apply.name}. The hiring team will review your resume for {selected.title}.
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-display text-2xl">{selected.title}</h2>
                <p className="text-sm text-secondary-500 mt-1 mb-4">
                  {labelFor(meta?.departments, selected.department)} · {labelFor(meta?.locations, selected.location)} ·{' '}
                  {labelFor(meta?.employment_types, selected.employment_type)} ·{' '}
                  {labelFor(meta?.experience_levels, selected.experience)}
                </p>
                <p className="whitespace-pre-wrap text-secondary-700">{selected.description}</p>
                {selected.requirements && (
                  <>
                    <h3 className="font-semibold mt-4 mb-1">What we look for</h3>
                    <p className="whitespace-pre-wrap text-secondary-700">{selected.requirements}</p>
                  </>
                )}
                <form className="mt-5 space-y-3" onSubmit={onApply}>
                  <h3 className="font-semibold">Apply</h3>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  <input
                    className="royal-input w-full"
                    placeholder="Full name"
                    required
                    value={apply.name}
                    onChange={(e) => setApply({ ...apply, name: e.target.value })}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      className="royal-input w-full"
                      type="email"
                      placeholder="Email"
                      required
                      value={apply.email}
                      onChange={(e) => setApply({ ...apply, email: e.target.value })}
                    />
                    <input
                      className="royal-input w-full"
                      placeholder="Mobile"
                      value={apply.phone}
                      onChange={(e) => setApply({ ...apply, phone: e.target.value })}
                    />
                  </div>
                  <textarea
                    className="royal-input w-full"
                    rows={4}
                    placeholder="Cover note"
                    value={apply.cover_letter}
                    onChange={(e) => setApply({ ...apply, cover_letter: e.target.value })}
                  />
                  <input type="file" accept={accept} required onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                  <button type="submit" className="royal-btn-primary w-full py-2.5" disabled={saving}>
                    {saving ? 'Sending…' : 'Submit application'}
                  </button>
                </form>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function LookupSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options?: CareerLookup[];
}) {
  return (
    <select className="royal-input" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      <option value="">Any {label.toLowerCase()}</option>
      {(options ?? []).map((item) => (
        <option key={item.code} value={item.code}>
          {item.label}
        </option>
      ))}
    </select>
  );
}
